import { useState, useEffect } from 'react'
import {
  Loader2,
  Plus,
  Send,
  Inbox,
  Mail,
  Shield,
  FileText,
  Building2,
  Users,
  MoreHorizontal,
  Eye,
  EyeOff,
  Clock,
  CheckCircle,
  Trash2,
  Edit,
  ExternalLink,
} from 'lucide-react'
import { legalAPI } from '@/utils/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

interface Correspondence {
  id: number
  party_type: string
  party_type_display: string
  party_name: string
  direction: string
  direction_display: string
  channel: string
  channel_display: string
  status: string
  status_display: string
  subject: string
  sent_at: string | null
  received_at: string | null
  response_deadline: string | null
  visible_to_client: boolean
  attachment_count: number
  created_by_name: string | null
  created_at: string
}

interface CorrespondenceListProps {
  caseId: number
  onNewCorrespondence?: () => void
  onViewCorrespondence?: (correspondenceId: number) => void
  onEditCorrespondence?: (correspondenceId: number) => void
}

const PARTY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  rsv: Shield,
  court: Building2,
  defendant: FileText,
  client: Users,
  other: Mail,
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      return `vor ${diffMinutes} Min.`
    }
    return `vor ${diffHours} Std.`
  }
  if (diffDays === 1) return 'gestern'
  if (diffDays < 7) return `vor ${diffDays} Tagen`
  return formatDate(dateString)
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'draft':
      return 'bg-slate-100 text-slate-600'
    case 'ready':
      return 'bg-amber-100 text-amber-700'
    case 'sent':
      return 'bg-highlight text-highlight-text'
    case 'delivered':
      return 'bg-green-100 text-green-700'
    case 'received':
      return 'bg-emerald-100 text-emerald-700'
    default:
      return 'bg-slate-100 text-slate-600'
  }
}

export function CorrespondenceList({
  caseId,
  onNewCorrespondence,
  onViewCorrespondence,
  onEditCorrespondence,
}: CorrespondenceListProps) {
  const [correspondences, setCorrespondences] = useState<Correspondence[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filterPartyType, setFilterPartyType] = useState<string>('all')
  const [filterDirection, setFilterDirection] = useState<string>('all')

  const fetchCorrespondences = async () => {
    setIsLoading(true)
    try {
      const options: { partyType?: string; direction?: string } = {}
      if (filterPartyType !== 'all') options.partyType = filterPartyType
      if (filterDirection !== 'all') options.direction = filterDirection

      const response = await legalAPI.getCorrespondences(caseId, options)
      setCorrespondences(response.correspondences || [])
    } catch (error) {
      console.error('Failed to fetch correspondences:', error)
      toast.error('Fehler beim Laden der Korrespondenz')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCorrespondences()
  }, [caseId, filterPartyType, filterDirection])

  const handleSend = async (correspondenceId: number) => {
    try {
      await legalAPI.sendCorrespondence(correspondenceId)
      toast.success('Korrespondenz als versendet markiert')
      fetchCorrespondences()
    } catch (error) {
      console.error('Failed to send correspondence:', error)
      toast.error('Fehler beim Versenden')
    }
  }

  const handleDelete = async (correspondenceId: number) => {
    if (!confirm('Korrespondenz wirklich löschen?')) return
    try {
      await legalAPI.deleteCorrespondence(correspondenceId)
      toast.success('Korrespondenz gelöscht')
      fetchCorrespondences()
    } catch (error) {
      console.error('Failed to delete correspondence:', error)
      toast.error('Fehler beim Löschen')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with filters and new button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Select value={filterPartyType} onValueChange={setFilterPartyType}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Alle Parteien" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Parteien</SelectItem>
              <SelectItem value="rsv">RSV</SelectItem>
              <SelectItem value="court">Gericht</SelectItem>
              <SelectItem value="defendant">Gegner</SelectItem>
              <SelectItem value="client">Mandant</SelectItem>
              <SelectItem value="other">Sonstige</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterDirection} onValueChange={setFilterDirection}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Alle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="outbound">Ausgehend</SelectItem>
              <SelectItem value="inbound">Eingehend</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button onClick={onNewCorrespondence} className="gap-2">
          <Plus className="h-4 w-4" />
          Neue Korrespondenz
        </Button>
      </div>

      {/* Correspondence list */}
      {correspondences.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <Mail className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">Keine Korrespondenz</h3>
          <p className="text-slate-500 text-sm mb-4">
            Erstellen Sie eine neue Korrespondenz, um die Kommunikation zu dokumentieren.
          </p>
          <Button onClick={onNewCorrespondence} variant="outline" className="gap-2">
            <Plus className="h-4 w-4" />
            Neue Korrespondenz
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {correspondences.map((corr) => {
            const PartyIcon = PARTY_ICONS[corr.party_type] || Mail
            const isOutbound = corr.direction === 'outbound'
            const isDraft = corr.status === 'draft'

            return (
              <div
                key={corr.id}
                className={cn(
                  'bg-white rounded-lg border border-slate-200 p-4 hover:border-slate-300 transition-colors',
                  isDraft && 'border-dashed'
                )}
              >
                <div className="flex items-start gap-3">
                  {/* Direction indicator */}
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                      isOutbound ? 'bg-highlight' : 'bg-green-100'
                    )}
                  >
                    {isOutbound ? (
                      <Send className={cn('h-5 w-5', 'text-highlight-text')} />
                    ) : (
                      <Inbox className={cn('h-5 w-5', 'text-green-600')} />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h4 className="font-medium text-slate-900 truncate">{corr.subject}</h4>
                      <Badge variant="outline" className="flex items-center gap-1 text-xs">
                        <PartyIcon className="h-3 w-3" />
                        {corr.party_type_display}
                      </Badge>
                      <Badge className={cn('text-xs', getStatusColor(corr.status))}>
                        {corr.status_display}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      {corr.party_name && (
                        <span>
                          {isOutbound ? 'An: ' : 'Von: '}
                          {corr.party_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Mail className="h-3.5 w-3.5" />
                        {corr.channel_display}
                      </span>
                      {corr.attachment_count > 0 && (
                        <span className="flex items-center gap-1">
                          <FileText className="h-3.5 w-3.5" />
                          {corr.attachment_count} Anhang{corr.attachment_count > 1 ? 'e' : ''}
                        </span>
                      )}
                    </div>

                    {corr.response_deadline && (
                      <div className="flex items-center gap-1 text-sm text-red-600 mt-1">
                        <Clock className="h-3.5 w-3.5" />
                        Frist: {formatDate(corr.response_deadline)}
                      </div>
                    )}
                  </div>

                  {/* Right side - date and actions */}
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'text-xs',
                          corr.visible_to_client ? 'text-green-600' : 'text-slate-400'
                        )}
                        title={corr.visible_to_client ? 'Für Mandant sichtbar' : 'Nicht sichtbar'}
                      >
                        {corr.visible_to_client ? (
                          <Eye className="h-3.5 w-3.5" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5" />
                        )}
                      </span>
                      <span className="text-xs text-slate-500">
                        {formatRelativeTime(corr.created_at)}
                      </span>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onViewCorrespondence?.(corr.id)}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Anzeigen
                        </DropdownMenuItem>
                        {isDraft && (
                          <DropdownMenuItem onClick={() => onEditCorrespondence?.(corr.id)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Bearbeiten
                          </DropdownMenuItem>
                        )}
                        {isOutbound && (corr.status === 'draft' || corr.status === 'ready') && (
                          <DropdownMenuItem onClick={() => handleSend(corr.id)}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Als versendet markieren
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(corr.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
